/**
 * GET call for the latest saved medical PA form data of a single order.
 * @module getLatestMedicalPaFormData
 */

import { API_ENDPOINTS } from "../../enums/apiUrls";
import { getBearerToken } from "../postCall/bearerToken";

/** Query parameters for {@link getLatestMedicalPaFormData}. */
export interface GetLatestMedicalPaFormDataParams {
  /** PA order ID whose latest form data should be fetched. */
  order_id: string;
  /** Organisation ID scoping the query. */
  org_id: string;
  /** Form/template ID (e.g. `aetna`) selected for the order. */
  form_id: string;
}

/**
 * Latest medical PA form-data payload returned by the endpoint.
 *
 * The concrete `form_data` shape is form-specific, so it is left open; refine
 * this interface once the response contract is finalised.
 */
export interface LatestMedicalPaFormData {
  [key: string]: unknown;
}

/**
 * Fetches the latest medical PA form data for an order/form combination.
 *
 * @param params - Order ID, org ID, and form ID sent as query parameters.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns The latest form-data payload for the given order and form.
 * @throws {Error} When the HTTP response is not OK (includes status in the message).
 * @throws Re-throws any network or parsing error after logging.
 */
export const getLatestMedicalPaFormData = async (
  params: GetLatestMedicalPaFormDataParams,
  signal?: AbortSignal,
): Promise<LatestMedicalPaFormData> => {
  try {
    const query = new URLSearchParams({
      order_id: params.order_id,
      org_id: params.org_id,
      form_id: params.form_id,
    }).toString();
    const url = `${API_ENDPOINTS.GET_LATEST_MEDICAL_PA_FORM_DATA}?${query}`;
    const bearerToken = await getBearerToken();
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      ...(signal && { signal }),
    });
    const result: LatestMedicalPaFormData = await response.json();
    if (!response.ok) {
      throw new Error(
        `Latest medical PA form-data request failed (${response.status}): ${JSON.stringify(result)}`,
      );
    }
    return result;
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.error(error);
    }
    throw error;
  }
};
